import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MetadataEditor } from './MetadataEditor';
import { PatternColorPicker } from './ActivityCalendar/PatternColorPicker';
import { deriveColor } from '../logic/patternColor';

interface ProjectDetailsFormProps {
  title: string;
  metadata: Record<string, string> | undefined;
  patternId?: string;
  initialColor?: string;
  onChange: (next: { title: string; metadata: Record<string, string>; color: string }) => void;
  onValidityChange?: (valid: boolean) => void;
}

export const ProjectDetailsForm: React.FC<ProjectDetailsFormProps> = ({
  title: initialTitle,
  metadata: initialMetadata,
  patternId,
  initialColor,
  onChange,
  onValidityChange,
}) => {
  const { t } = useTranslation('library');
  const seedTitle = useMemo(() => initialTitle, []); // eslint-disable-line react-hooks/exhaustive-deps
  const seedMetadata = useMemo(() => initialMetadata, []); // eslint-disable-line react-hooks/exhaustive-deps

  const seedColor = useMemo(
    () => initialColor ?? (patternId ? deriveColor(patternId) : ''),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const [color, setColor] = useState(seedColor);

  const [title, setTitle] = useState(seedTitle);
  const [metadata, setMetadata] = useState<Record<string, string>>(
    seedMetadata ? { ...seedMetadata } : {},
  );
  const [metadataValid, setMetadataValid] = useState(true);

  const titleValid = title.trim() !== '';
  const valid = titleValid && metadataValid;

  useEffect(() => {
    onChange({ title, metadata, color });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, metadata, color]);

  useEffect(() => {
    onValidityChange?.(valid);
  }, [valid, onValidityChange]);

  return (
    <div className="space-y-4">
      <label className="block">
        <span className="text-[10px] font-bold uppercase tracking-widest text-ink-faint">
          {t('projectDetails.nameLabel', 'Project name')}
        </span>
        <input
          aria-label={t('projectDetails.nameLabel', 'Project name')}
          type="text"
          autoFocus
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="mt-2 block w-full px-4 py-3 bg-base border border-line rounded-2xl text-base font-bold text-ink focus:ring-2 focus:ring-primary focus:border-transparent"
        />
      </label>
      <MetadataEditor
        metadata={seedMetadata}
        onChange={setMetadata}
        onValidityChange={setMetadataValid}
      />
      {patternId && (
        <PatternColorPicker
          patternId={patternId}
          value={color}
          onChange={setColor}
        />
      )}
    </div>
  );
};
